// src/KnowledgeBasePage.js
import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import './KnowledgeBasePage.css';

function KnowledgeBasePage() {
  const { t, lang, toggleLang } = useI18n();
  const navigate = useNavigate();
  
  const [currentUser, setCurrentUser] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [loading, setLoading] = useState(true);
  
  // بيانات قاعدة المعرفة
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  
  // بيانات الفورم
  const [questionText, setQuestionText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [productContext, setProductContext] = useState(''); // اختياري
  
  // حالة الإضافة
  const [isAdding, setIsAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // حالة التعديل
  const [editingItem, setEditingItem] = useState(null);
  
  // فلتر البحث
  const [searchTerm, setSearchTerm] = useState('');

  // التحقق من تسجيل الدخول
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser(user);
        fetchBrandName(user.uid);
      } else {
        navigate('/login');
      }
    });
    return unsubscribe;
  }, [navigate]);

  // جلب اسم البراند
  const fetchBrandName = async (uid) => {
    try {
      const clientsRef = collection(db, 'clients_api');
      const q = query(clientsRef, where('brand_owner', '==', uid));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const clientData = snapshot.docs[0].data();
        setBrandName(snapshot.docs[0].id);
        fetchKnowledgeBase(snapshot.docs[0].id);
      } else {
        alert('لم يتم العثور على معلومات البراند');
        setLoading(false);
      }
    } catch (error) {
      console.error('خطأ في جلب اسم البراند:', error);
      alert('حدث خطأ في تحميل البيانات');
      setLoading(false);
    }
  };

  // جلب بيانات قاعدة المعرفة
  const fetchKnowledgeBase = (brand) => {
    const knowledgeRef = collection(db, 'knowledge_base_api');
    const q = query(knowledgeRef, where('brand_name', '==', brand));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // ترتيب حسب التاريخ (الأحدث أولاً)
      items.sort((a, b) => {
        const dateA = a.date_added?.toDate?.() || new Date(0);
        const dateB = b.date_added?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      
      setKnowledgeItems(items);
      setFilteredItems(items);
      setLoading(false);
    }, (error) => {
      console.error('خطأ في جلب قاعدة المعرفة:', error);
      alert('حدث خطأ في تحميل قاعدة المعرفة');
      setLoading(false);
    });
    
    return unsubscribe;
  };

  // فلترة البيانات حسب البحث
  useEffect(() => {
    if ((searchTerm || '').trim() === '') {
      setFilteredItems(knowledgeItems);
    } else {
      const filtered = knowledgeItems.filter(item => 
        item.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product_context?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, knowledgeItems]);

  // إضافة سؤال وإجابة جديدة
  const handleAddKnowledge = async (e) => {
    e.preventDefault();
    
    if (!(questionText || '').trim() || !(answerText || '').trim()) {
      alert('يرجى ملء السؤال والإجابة');
      return;
    }
    
    setIsAdding(true);
    try {
      const knowledgeRef = collection(db, 'knowledge_base_api');
      await addDoc(knowledgeRef, {
        brand_name: brandName,
        question_text: (questionText || '').trim(),
        answer_text: (answerText || '').trim(),
        product_context: (productContext || '').trim() || '', // اختياري
        date_added: serverTimestamp()
      });
      
      // إعادة تعيين الفورم
      setQuestionText('');
      setAnswerText('');
      setProductContext('');
      setShowAddForm(false);
      alert('✅ تم إضافة السؤال والإجابة بنجاح!');
    } catch (error) {
      console.error('خطأ في إضافة السؤال:', error);
      alert('حدث خطأ في إضافة السؤال. حاول مرة أخرى.');
    } finally {
      setIsAdding(false);
    }
  };

  // حذف سؤال
  const handleDeleteKnowledge = async (itemId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'knowledge_base_api', itemId));
      alert('✅ تم الحذف بنجاح!');
    } catch (error) {
      console.error('خطأ في الحذف:', error);
      alert('حدث خطأ في الحذف. حاول مرة أخرى.');
    }
  };

  // بدء التعديل
  const handleStartEdit = (item) => {
    setEditingItem({
      id: item.id,
      question_text: item.question_text,
      answer_text: item.answer_text,
      product_context: item.product_context || ''
    });
  };

  // حفظ التعديلات
  const handleSaveEdit = async () => {
    if (!(editingItem.question_text || '').trim() || !(editingItem.answer_text || '').trim()) {
      alert('يرجى ملء السؤال والإجابة');
      return;
    }
    
    try {
      const docRef = doc(db, 'knowledge_base_api', editingItem.id);
      await updateDoc(docRef, {
        question_text: (editingItem.question_text || '').trim(),
        answer_text: (editingItem.answer_text || '').trim(),
        product_context: (editingItem.product_context || '').trim() || ''
      });
      
      setEditingItem(null);
      alert('✅ تم التعديل بنجاح!');
    } catch (error) {
      console.error('خطأ في التعديل:', error);
      alert('حدث خطأ في التعديل. حاول مرة أخرى.');
    }
  };

  // إلغاء التعديل
  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  // تنسيق التاريخ
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="knowledge-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>جاري تحميل قاعدة المعرفة...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-page">
      {/* الهيدر */}
      <div className="knowledge-header">
        <div className="header-content">
          <div className="header-title">
            <h1>📚 قاعدة المعرفة (الردود الآلية)</h1>
            <p className="brand-name">البراند: <strong>{brandName}</strong></p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-back"
              onClick={() => navigate('/dashboard')}
            >
              ← رجوع للداشبورد
            </button>
            <button 
              className="btn-add"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? '✖ إلغاء' : '➕ إضافة سؤال جديد'}
            </button>
          </div>
        </div>
      </div>

      {/* فورم الإضافة */}
      {showAddForm && (
        <div className="add-form-container">
          <form onSubmit={handleAddKnowledge} className="add-form">
            <h3>إضافة سؤال وإجابة جديدة</h3>
            
            <div className="form-group">
              <label>السؤال <span className="required">*</span></label>
              <input
                type="text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="مثال: هل المنتج متوفر؟"
                required
              />
            </div>
            
            <div className="form-group">
              <label>الإجابة <span className="required">*</span></label>
              <textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="مثال: نعم المنتج متوفر ويمكنك الطلب الآن"
                rows="4"
                required
              />
            </div>
            
            <div className="form-group">
              <label>المنتج (اختياري)</label>
              <input
                type="text"
                value={productContext}
                onChange={(e) => setProductContext(e.target.value)}
                placeholder="مثال: Example Jeans - 28 (الكمية 1)"
              />
              <small className="form-hint">
                💡 إذا تركت هذا الحقل فارغاً، سيطبق السؤال على جميع المنتجات
              </small>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={() => {
                  setShowAddForm(false);
                  setQuestionText('');
                  setAnswerText('');
                  setProductContext('');
                }}
              >
                إلغاء
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isAdding}
              >
                {isAdding ? 'جاري الإضافة...' : '✅ حفظ'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* البحث */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 ابحث في الأسئلة والإجابات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <p className="results-count">
          عرض {filteredItems.length} من أصل {knowledgeItems.length} سؤال
        </p>
      </div>

      {/* قائمة الأسئلة */}
      <div className="knowledge-list">
        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <p>📭 لا توجد أسئلة في قاعدة المعرفة</p>
            <p className="empty-hint">ابدأ بإضافة أول سؤال وإجابة!</p>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item.id} className="knowledge-item">
              {editingItem && editingItem.id === item.id ? (
                // وضع التعديل
                <div className="edit-form">
                  <div className="form-group">
                    <label>السؤال</label>
                    <input
                      type="text"
                      value={editingItem.question_text}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        question_text: e.target.value
                      })}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>الإجابة</label>
                    <textarea
                      value={editingItem.answer_text}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        answer_text: e.target.value
                      })}
                      rows="4"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>المنتج (اختياري)</label>
                    <input
                      type="text"
                      value={editingItem.product_context}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        product_context: e.target.value
                      })}
                    />
                  </div>
                  
                  <div className="edit-actions">
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      إلغاء
                    </button>
                    <button className="btn-submit" onClick={handleSaveEdit}>
                      ✅ حفظ التعديلات
                    </button>
                  </div>
                </div>
              ) : (
                // وضع العرض
                <>
                  <div className="item-header">
                    <div className="item-meta">
                      <span className="item-date">📅 {formatDate(item.date_added)}</span>
                      {item.product_context && (
                        <span className="product-badge">
                          📦 {item.product_context}
                        </span>
                      )}
                    </div>
                    <div className="item-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleStartEdit(item)}
                        title="تعديل"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteKnowledge(item.id)}
                        title="حذف"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <div className="item-content">
                    <div className="question-section">
                      <h4>❓ السؤال:</h4>
                      <p>{item.question_text}</p>
                    </div>
                    
                    <div className="answer-section">
                      <h4>✅ الإجابة:</h4>
                      <p>{item.answer_text}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* الفوتر */}
      <div className="knowledge-footer">
        <p>💡 نصيحة: يمكنك ترك حقل "المنتج" فارغاً ليطبق السؤال على جميع المنتجات</p>
      </div>
    </div>
  );
}

export default KnowledgeBasePage;
